import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { PageHeader, Empty } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Download, EnvelopeSimple, Trash, DotsThreeVertical, MagnifyingGlass, FileText, WarningCircle } from "@phosphor-icons/react";

const KIND_LABEL = { client: "Client", interpreter: "Interpreter" };

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }); } catch { return iso; }
};
const fmtMoney = (n) => (n == null || n === "") ? "—" : `$${Number(n || 0).toFixed(2)}`;

export default function Contracts() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  const { data: contracts = [], isLoading } = useQuery({
    queryKey: ["contracts"],
    queryFn: async () => (await api.get("/contracts")).data,
  });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: async () => (await api.get("/clients")).data });
  const { data: interpreters = [] } = useQuery({ queryKey: ["interpreters"], queryFn: async () => (await api.get("/interpreters")).data });

  const clientById = useMemo(() => Object.fromEntries(clients.map((c) => [c.client_id, c])), [clients]);
  const intpById = useMemo(() => Object.fromEntries(interpreters.map((i) => [i.interpreter_id, i])), [interpreters]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contracts.filter((c) => {
      if (kind !== "all" && c.kind !== kind) return false;
      if (status === "signed" && !c.signed_at) return false;
      if (status === "unsigned" && c.signed_at) return false;
      if (!q) return true;
      const partyName = c.kind === "client"
        ? clientById[c.client_id]?.name
        : intpById[c.interpreter_id]?.full_name;
      const hay = `${c.contract_number || ""} ${partyName || ""} ${c.language || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [contracts, kind, status, search, clientById, intpById]);

  const totals = useMemo(() => ({
    total: contracts.length,
    signed: contracts.filter((c) => c.signed_at).length,
    unsigned: contracts.filter((c) => !c.signed_at).length,
    clientContracts: contracts.filter((c) => c.kind === "client").length,
    interpreterContracts: contracts.filter((c) => c.kind === "interpreter").length,
  }), [contracts]);

  const resend = useMutation({
    mutationFn: async (contract_id) => (await api.post(`/contracts/${contract_id}/email`, {})).data,
    onSuccess: (d) => toast.success(`Emailed to ${d?.sent_to || "recipient"}`),
    onError: (e) => toast.error(e?.response?.data?.detail || "Email failed"),
  });

  const del = useMutation({
    mutationFn: async (contract_id) => (await api.delete(`/contracts/${contract_id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      setToDelete(null);
      setConfirmText("");
      toast.success("Contract permanently deleted");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const download = async (c) => {
    const url = `${API}/contracts/${c.contract_id}/download`;
    try {
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) return toast.error("Download failed");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${c.contract_number}.docx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div data-testid="contracts-page">
      <PageHeader
        eyebrow="Legal"
        title="Contracts"
        description="Every generated and signed agreement — client Interpreter Services Agreements and interpreter 1099 Contractor Agreements."
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <StatCard label="Total" value={totals.total} />
        <StatCard label="Signed" value={totals.signed} tone="emerald" />
        <StatCard label="Awaiting signature" value={totals.unsigned} tone="amber" />
        <StatCard label="Client" value={totals.clientContracts} />
        <StatCard label="Interpreter" value={totals.interpreterContracts} />
      </div>

      <div className="card-flat p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="contracts-filters">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by contract # or name…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="contract-search"
          />
        </div>
        <div className="min-w-[150px]">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="filter-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All kinds</SelectItem>
              <SelectItem value="client">Client contracts</SelectItem>
              <SelectItem value="interpreter">Interpreter contracts</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[150px]">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="signed">Signed</SelectItem>
              <SelectItem value="unsigned">Awaiting signature</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-slate-500 ml-auto">
          Showing <b>{filtered.length}</b> of <b>{contracts.length}</b>
        </div>
      </div>

      {isLoading ? (
        <div className="card-flat p-10 text-center text-slate-400">Loading…</div>
      ) : filtered.length === 0 ? (
        <Empty title="No contracts match" hint="Try clearing the filters or generating a new contract from a project or interpreter." />
      ) : (
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm" data-testid="contracts-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Contract #</th>
                <th className="text-left px-3 py-3 font-semibold">Kind</th>
                <th className="text-left px-3 py-3 font-semibold">Party</th>
                <th className="text-left px-3 py-3 font-semibold">Language</th>
                <th className="text-left px-3 py-3 font-semibold">Status</th>
                <th className="text-left px-3 py-3 font-semibold">Signed</th>
                <th className="text-right px-3 py-3 font-semibold">Amount</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const partyName = c.kind === "client"
                  ? clientById[c.client_id]?.name || "—"
                  : intpById[c.interpreter_id]?.full_name || "—";
                const signed = !!c.signed_at;
                return (
                  <tr key={c.contract_id} className="data-row border-t border-slate-100" data-testid={`contract-row-${c.contract_id}`}>
                    <td className="px-5 py-3 font-mono text-xs text-slate-800">{c.contract_number}</td>
                    <td className="px-3 py-3 text-slate-700">
                      <span className={`badge-pill ${c.kind === "client" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-purple-50 text-purple-700 border-purple-200"}`}>
                        <FileText size={11} className="inline mr-1" /> {KIND_LABEL[c.kind] || c.kind}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-slate-800">{partyName}</td>
                    <td className="px-3 py-3 text-slate-600 text-xs uppercase">{c.language || "en"}</td>
                    <td className="px-3 py-3">
                      {signed
                        ? <span className="badge-pill bg-emerald-50 text-emerald-700 border-emerald-200">Signed</span>
                        : <span className="badge-pill bg-amber-50 text-amber-700 border-amber-200">Awaiting</span>}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600">{fmtDate(c.signed_at)}</td>
                    <td className="px-3 py-3 text-right text-slate-800">{fmtMoney(c.amount)}</td>
                    <td className="px-5 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`contract-actions-${c.contract_id}`}>
                            <DotsThreeVertical size={16} weight="bold" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52 bg-white">
                          <DropdownMenuLabel className="text-xs text-slate-500">Contract actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => download(c)} data-testid={`contract-download-${c.contract_id}`}>
                            <Download size={14} className="mr-2" /> Download DOCX
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => resend.mutate(c.contract_id)} data-testid={`contract-resend-${c.contract_id}`}>
                            <EnvelopeSimple size={14} className="mr-2" /> Resend email
                          </DropdownMenuItem>
                          {isAdmin && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setToDelete(c)}
                                className="text-red-600"
                                data-testid={`contract-delete-${c.contract_id}`}
                              >
                                <Trash size={14} className="mr-2" /> Delete contract
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation dialog — requires typing the contract number to prevent accidents */}
      <Dialog open={!!toDelete} onOpenChange={(o) => { if (!o) { setToDelete(null); setConfirmText(""); } }}>
        <DialogContent className="bg-white max-w-md" data-testid="delete-contract-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <WarningCircle size={20} weight="fill" /> Permanently delete contract
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-3">
            <p>
              This will permanently delete <b>{toDelete?.contract_number}</b> and its signed copy from the database. This action <b>cannot be undone</b>.
            </p>
            {toDelete?.signed_at && (
              <div className="p-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-xs">
                Heads up — this contract was <b>signed on {fmtDate(toDelete.signed_at)}</b>. Deleting it removes the audit trail (signature, IP, user-agent, timestamp).
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1">
                Type the contract number to confirm
              </label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={toDelete?.contract_number || ""}
                data-testid="delete-contract-confirm-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setToDelete(null); setConfirmText(""); }}>Cancel</Button>
            <Button
              onClick={() => toDelete && del.mutate(toDelete.contract_id)}
              disabled={del.isPending || confirmText.trim() !== toDelete?.contract_number}
              className="bg-red-700 hover:bg-red-800 text-white disabled:opacity-50"
              data-testid="delete-contract-confirm-btn"
            >
              {del.isPending ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, tone = "slate" }) {
  const toneCls = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    amber: "text-amber-700",
  }[tone] || "text-slate-900";
  return (
    <div className="card-flat p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${toneCls}`}>{value}</div>
    </div>
  );
}
